import { useFormikContext } from "formik";
import { CURRENT_COVERAGE_FORM } from "pages/PreQualification/utils/constants";
import { CurrentCoverageFormType } from "pages/PreQualification/utils/formTypes";
import { healthCarrierOptions } from "pages/PreQualification/utils/selectHelper";
import React, { useEffect } from "react";
import DateInput from "uicomponents/DateInput";
import RadioInput from "uicomponents/RadioInput";
import SelectInput from "uicomponents/SelectInput";
import TextArea from "uicomponents/TextArea";
import TextInput from "uicomponents/TextInput";
import { hasQueryParam } from "utils/helper";
import { SystemIcon } from "@justworkshr/milo-components";
import styles from "../CurrentCoverage.module.css";
import { peoOptions } from "pages/CensusAndCurrentInformation/utils/helper";

interface ICurrentCoverageComponentProps {
  data?: any;
}

const CurrentCoverageComponent = ({
  ...props
}: ICurrentCoverageComponentProps): JSX.Element => {
  const { values, setFieldValue } = useFormikContext();

  const lableList = CURRENT_COVERAGE_FORM.QUESTIONS;
  const messageList = CURRENT_COVERAGE_FORM.MESSAGE;

  const hasHealthInsurance = (values as CurrentCoverageFormType)
    ?.currentlyOfferHealthInsurance;
  const hasCobraInsurance = (values as CurrentCoverageFormType)?.cobraUsage;
  const hasOtherCarier = (values as CurrentCoverageFormType)?.currentCarrier;

  const isOnPeo = (values as CurrentCoverageFormType)?.isOnPeo;
  const hasCurrentPeo = (values as CurrentCoverageFormType)?.currentPeo;

  const hi_flow_with_core_app = hasQueryParam("usecoreapp");

  useEffect(() => {
    if (hasHealthInsurance === "No") {
      const updateFieldList = [
        "currentCarrier",
        "currentRenewalDate",
        "cobraUsage",
        "cobraUsers",
        "benefitEligibleEmployees",
        "currentCarrierOther",
      ];
      updateFieldList.forEach((field) => {
        setFieldValue(field, "");
      });
    }
  }, [hasHealthInsurance, setFieldValue]);

  useEffect(() => {
    if (hasCobraInsurance === "No") {
      const updateFieldList = ["cobraUsers", "benefitEligibleEmployees"];
      updateFieldList.forEach((field) => {
        setFieldValue(field, "");
      });
    }
  }, [hasCobraInsurance, setFieldValue]);

  useEffect(() => {
    if (hasOtherCarier !== "OTHER") {
      setFieldValue("currentCarrierOther", "");
    }
  }, [hasOtherCarier, setFieldValue]);

  useEffect(() => {
    if (isOnPeo === "No") {
      const updateFieldList = ["currentPeo", "currentPeoOther"];
      updateFieldList.forEach((field) => {
        setFieldValue(field, "");
      });
    }
  }, [isOnPeo, setFieldValue]);

  useEffect(() => {
    if (hasCurrentPeo !== "Other") {
      setFieldValue("currentPeoOther", "");
    }
  }, [hasCurrentPeo, setFieldValue]);

  return (
    <div>
      {hi_flow_with_core_app && (
        <>
          <TextInput
            name="sicCode"
            label="What is your company’s Standard Industrial Classification (SIC) code?"
          />
          <a
            href="https://siccode.com/sic-code-lookup-directory"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
          >
            Find your company’s code
            <SystemIcon
              iconName="external-link"
              color="brand"
              title="person"
              size="small"
            />
          </a>
        </>
      )}
      {!hi_flow_with_core_app && (
        <RadioInput
          name="currentlyOfferHealthInsurance"
          label={lableList.currentlyOfferHealthInsurance}
          options={["Yes", "No"]}
        />
      )}
      {hasHealthInsurance === "Yes" && (
        <>
          <SelectInput
            name="currentCarrier"
            options={healthCarrierOptions}
            label={lableList.currentCarrier}
            placeholder="Select an option"
          />
          {hasOtherCarier === "OTHER" && (
            <TextInput
              name="currentCarrierOther"
              label={lableList.currentCarrierOther}
            />
          )}
          <DateInput
            name="currentRenewalDate"
            label={lableList.currentRenewalDate}
          />
          <RadioInput
            name="cobraUsage"
            label={lableList.cobraUsage}
            options={["Yes", "No"]}
          />
          {hasCobraInsurance === "Yes" && (
            <>
              <TextInput name="cobraUsers" label={lableList.cobraUsers} />
              <TextInput
                name="benefitEligibleEmployees"
                label={lableList.benefitEligibleEmployees}
                message={messageList.benefitEligibleEmployees}
              />
            </>
          )}
        </>
      )}
      {hi_flow_with_core_app && (
        <>
          <RadioInput
            name="controlledGroup"
            label="Are you part of a controlled group?"
            options={["Yes", "No"]}
          />
          <a
            href="https://help.justworks.com/hc/en-us/articles/360018609291-Related-Entities"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
          >
            Learn about controlled groups
            <SystemIcon
              iconName="external-link"
              color="brand"
              title="person"
              size="small"
            />
          </a>
          <RadioInput
            name="isOnPeo"
            label={"Are you currently on a PEO?"}
            options={["Yes", "No"]}
          />
          <a
            href="https://www.justworks.com/blog/whats-a-peo"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
          >
            Learn about PEOs
            <SystemIcon
              iconName="external-link"
              color="brand"
              title="person"
              size="small"
            />
          </a>
          {isOnPeo === "Yes" && (
            <>
              <SelectInput
                name="currentPeo"
                options={peoOptions}
                label="Which PEO?"
                placeholder="Select an option"
              />
              {hasCurrentPeo === "Other" && (
                <TextArea name="currentPeoOther" label="Please specify" />
              )}
            </>
          )}
          <DateInput
            name="startingCoverageDate"
            label="When will you need your coverage to begin?"
          />
        </>
      )}
    </div>
  );
};

export default CurrentCoverageComponent;
